// Findings #200 follow-up: "Lote" filter in Base de datos. One option per lot
// that has rows in the table; lots that share a name are told apart without
// internal codes: "(lote eliminado)", "(trasladado)" or "desde <first day>".

type FilterRow = { id?: unknown; lotId?: unknown; lot?: unknown; date?: unknown }

export type LotFilterOption = { value: string; label: string }

const text = (value: unknown): string => String(value ?? '').trim()

/** The filter value of a row: its lot id, else its lot name. Empty without either. */
export const lotFilterValue = (row: FilterRow): string => {
  const lotId = text(row.lotId)
  if (lotId) return `id:${lotId}`
  const name = text(row.lot).toLowerCase()
  return name ? `label:${name}` : ''
}

export const buildLotFilterOptions = (args: {
  rows: ReadonlyArray<FilterRow>
  dayOf: (row: FilterRow) => string
  deletedRowIds: ReadonlySet<string>
  movedRowIds: ReadonlySet<string>
  labels: { deleted: string; moved: string; from: (day: string) => string }
}): LotFilterOption[] => {
  type Group = { value: string; name: string; firstDay: string; rows: number; deleted: number; moved: number }
  const groups = new Map<string, Group>()
  for (const row of args.rows) {
    const value = lotFilterValue(row)
    const name = text(row.lot)
    if (!value || !name) continue
    const group = groups.get(value) ?? { value, name, firstDay: '', rows: 0, deleted: 0, moved: 0 }
    const day = args.dayOf(row)
    if (day && (!group.firstDay || day < group.firstDay)) group.firstDay = day
    group.rows += 1
    const rowId = text(row.id)
    if (args.deletedRowIds.has(rowId)) group.deleted += 1
    else if (args.movedRowIds.has(rowId)) group.moved += 1
    groups.set(value, group)
  }

  const nameCount = new Map<string, number>()
  groups.forEach((group) => {
    const key = group.name.toLowerCase()
    nameCount.set(key, (nameCount.get(key) ?? 0) + 1)
  })

  return Array.from(groups.values())
    .sort((a, b) => a.name.localeCompare(b.name) || a.firstDay.localeCompare(b.firstDay))
    .map((group) => {
      if ((nameCount.get(group.name.toLowerCase()) ?? 0) < 2) return { value: group.value, label: group.name }
      if (group.deleted === group.rows) return { value: group.value, label: `${group.name} ${args.labels.deleted}` }
      if (group.moved === group.rows) return { value: group.value, label: `${group.name} ${args.labels.moved}` }
      return { value: group.value, label: group.firstDay ? `${group.name} · ${args.labels.from(group.firstDay)}` : group.name }
    })
}
